/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import { ICwsFormData } from './cws-form.data';
import { ICwsFormProps } from './cws-form.props.autogenerated';

interface IFormState {
    salutation: string;
    firstName: string;
    lastName: string;
    email: string;
    countryCode: string;
    phone: string;
    customerNumber: string;
    subject: string;
    product: string;
    comment: string;
    addressStreet?: string;
    addressPostalCode?: string;
    addressCity?: string;
    suppliedCompany?: string;
}

interface IFormErrors {
    [key: string]: string;
}

interface ISubmitResponse {
    success?: boolean | string;
    Success?: boolean | string;
    Sucess?: boolean | string;
    errorCode?: string | null;
    ErrorCode?: string | null;
    message?: string | null;
    Message?: string | null;
}

type ICwsFormModuleProps = Omit<ICwsFormProps<ICwsFormData>, 'resources'> & {
    resources: ICwsFormProps<ICwsFormData>['resources'] & {
        formLabelSalutation: string;
        formSalutationFemale: string;
        formSalutationMale: string;
        formSalutationNone: string;
        formErrorSalutationRequired: string;
        formSubmitSuccessAlertMessage: string;
        formSubmitErrorAlertMessage: string;
        formSubmitUnknownErrorCode: string;
        formSubmitPhoneFormatErrorAlertMessage: string;
    };
};

export interface ICwsFormViewProps extends ICwsFormModuleProps {
    form: IFormState;
    errors: IFormErrors;
    submitted: boolean;
    submitStatus: 'idle' | 'success' | 'error';
    submitErrorCode?: string;
    className?: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
    onSubmit: (e: React.FormEvent) => void;
}

interface ICwsFormState {
    form: IFormState;
    errors: IFormErrors;
    submitted: boolean;
    submitStatus: 'idle' | 'success' | 'error';
    submitErrorCode?: string;
}

const initialState: IFormState = {
    salutation: '',
    firstName: '',
    lastName: '',
    email: '',
    countryCode: '',
    phone: '',
    customerNumber: '',
    subject: '',
    product: '',
    comment: '',
    addressStreet: '',
    addressPostalCode: '',
    addressCity: '',
    suppliedCompany: ''
};

const CREATE_CASE_FROM_ONLINE_SHOP_PATH = 'CreateCaseFromOnlineShop';

/**
 *
 * CwsForm component
 * @extends {React.PureComponent<ICwsFormProps<ICwsFormData>>}
 */
class CwsForm extends React.PureComponent<ICwsFormModuleProps, ICwsFormState> {
    private readonly priority = 'Medium';
    private readonly status = 'New';

    private readonly isSubmitResponseSuccessful = (submitResponse?: ISubmitResponse): boolean => {
        const successValue = submitResponse?.success ?? submitResponse?.Success ?? submitResponse?.Sucess;

        return successValue === true || successValue === 'true';
    };

    private readonly getSubmitErrorCode = (submitResponse?: ISubmitResponse): string | undefined => {
        const errorCode = submitResponse?.errorCode ?? submitResponse?.ErrorCode ?? submitResponse?.message ?? submitResponse?.Message;

        if (errorCode === null || errorCode === undefined) {
            return undefined;
        }

        const normalizedErrorCode = String(errorCode).trim();

        return normalizedErrorCode || undefined;
    };

    private readonly _getLevelsBySubject = (subject: string): { level1: string; level2: string } => {
        const {
            formSubjectProductRequestValue,
            formSubjectOfferRequestValue,
            formSubjectOrderRequestValue,
            formSubjectReturnRequestValue,
            formSubjectInvoiceRequestValue,
            formSubjectOtherValue
        } = this.props.resources;

        if (subject === formSubjectProductRequestValue) {
            return { level1: '320', level2: '699' };
        }

        if (subject === formSubjectOfferRequestValue) {
            return { level1: '318', level2: '869' };
        }

        if (subject === formSubjectOrderRequestValue) {
            return { level1: '318', level2: '730' };
        }

        if (subject === formSubjectReturnRequestValue) {
            return { level1: '322', level2: '741' };
        }

        if (subject === formSubjectInvoiceRequestValue) {
            return { level1: '323', level2: '718' };
        }

        if (subject === formSubjectOtherValue) {
            return { level1: '', level2: '' };
        }

        return { level1: '', level2: '' };
    };

    private readonly sanitizeProductValue = (value: string): string =>
        value
            .replace(/[<>"'`\\]/g, '')
            .replace(/\s+/g, ' ')
            .trim()
            .slice(0, 100);

    public state: ICwsFormState = {
        form: initialState,
        errors: {},
        submitted: false,
        submitStatus: 'idle',
        submitErrorCode: undefined
    };

    public componentDidMount(): void {
        this.prefillFromUser();
    }

    private prefillFromUser = (): void => {
        const { context } = this.props;
        const user = context.actionContext.requestContext.user;
        const productQueryParam = context.request.query?.product;
        const product = typeof productQueryParam === 'string' ? this.sanitizeProductValue(productQueryParam) : '';

        // Always set product from URL if present
        this.setState(prevState => ({
            form: {
                ...prevState.form,
                product: prevState.form.product || product
            }
        }));

        // Only prefill user data if authenticated
        if (!user || !context.request.user.isAuthenticated) {
            return;
        }

        const fullName = (user.name || '').trim();
        const [firstName = '', ...lastNameParts] = fullName ? fullName.split(/\s+/) : [];
        const lastName = lastNameParts.join(' ');
        const email = user.emailAddress || '';
        const customerNumber = user.customerAccountNumber || '';

        this.setState(prevState => ({
            form: {
                ...prevState.form,
                firstName: prevState.form.firstName || firstName,
                lastName: prevState.form.lastName || lastName,
                email: prevState.form.email || email,
                customerNumber: prevState.form.customerNumber || customerNumber
            }
        }));
    };

    private validate = (form: IFormState): IFormErrors => {
        const { resources } = this.props;
        const newErrors: IFormErrors = {};
        if (!form.salutation.trim()) {
            newErrors.salutation = resources.formErrorSalutationRequired;
        }
        if (!form.firstName.trim()) {
            newErrors.firstName = resources.formErrorFirstNameRequired;
        }
        if (!form.lastName.trim()) {
            newErrors.lastName = resources.formErrorLastNameRequired;
        }
        if (!form.email.trim()) {
            newErrors.email = resources.formErrorEmailRequired;
        } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
            newErrors.email = resources.formErrorEmailInvalid;
        }
        if (!form.subject || !form.subject.trim()) {
            newErrors.subject = resources.formErrorSubjectRequired;
        }
        if (!form.comment.trim()) {
            newErrors.comment = resources.formErrorCommentRequired;
        }
        return newErrors;
    };

    private handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
        const fieldName = e.target.name || e.target.id;
        let fieldValue = e.target.value;
        if (fieldName === 'phone') {
            fieldValue = fieldValue.replace(/\D/g, '');
        } else if (fieldName === 'email') {
            fieldValue = fieldValue.trim();
        } else if (fieldName === 'product') {
            fieldValue = this.sanitizeProductValue(fieldValue);
        }
        // No special handling needed for address fields
        this.setState(prevState => {
            const form = { ...prevState.form, [fieldName]: fieldValue };
            const errors = prevState.submitted ? this.validate(form) : prevState.errors;
            return { ...prevState, form, errors };
        });
    };

    private handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        const { apiSettings, user } = this.props.context.actionContext.requestContext;
        const formEndpoint = `${apiSettings.baseUrl}Commerce/${CREATE_CASE_FROM_ONLINE_SHOP_PATH}`;
        const validationErrors = this.validate(this.state.form);

        this.setState({
            submitted: true,
            submitStatus: 'idle',
            submitErrorCode: undefined,
            errors: validationErrors
        });

        if (Object.keys(validationErrors).length === 0) {
            const levels = this._getLevelsBySubject(this.state.form.subject);
            const caseCountryCode = String(
                (this.props.context.actionContext.requestContext as any)?.channel?.ChannelCountryRegionISOCode || ''
            );

            // Build address object if enabled
            let address;
            if (this.props.config.showAddressFields) {
                address = {
                    street: this.state.form.addressStreet || '',
                    postalCode: this.state.form.addressPostalCode || '',
                    city: this.state.form.addressCity || '',
                    SuppliedCompany: this.state.form.suppliedCompany || ''
                };
            }

            const payload: any = {
                SalesForceForm: {
                    PayLoad: {
                        Subject: this.state.form.subject,
                        Description: this.state.form.comment,
                        Origin: this.props.config.origin,
                        Status: this.status,
                        Priority: this.priority,
                        ERP_Order__c: '',
                        Level_1__c: levels.level1,
                        Level_2__c: levels.level2,
                        Solution_Area__c: this.props.config.solution_area__c,
                        Service_Line__c: this.props.config.service_line__c,
                        Order_Number__c: '',
                        X_Case_country__c: caseCountryCode,
                        product: {
                            id: this.state.form.product
                        },
                        contact: {
                            FirstName: this.state.form.firstName,
                            LastName: this.state.form.lastName,
                            Salutation: this.state.form.salutation,
                            Email: this.state.form.email,
                            Phone: `${this.state.form.countryCode || ''}${this.state.form.phone.replace(/^0+/, '')}`.trim()
                        },
                        erpData: {
                            IsPublic: !this.props.context.request.user?.isAuthenticated,
                            ERPDataName: this.state.form.customerNumber || '',
                            OrganisationName:
                                (this.props.context.actionContext.requestContext as any)?.channel?.InventLocationDataAreaId || ''
                        }
                    }
                }
            };
            if (address) {
                payload.SalesForceForm.PayLoad.address = address;
            }
            try {
                const headers: Record<string, string> = {
                    oun: apiSettings.oun,
                    'Content-Type': 'application/json'
                };
                if (user.token) {
                    headers.Authorization = `id_token ${user.token}`;
                }

                const response = await Msdyn365.sendRequest<ISubmitResponse>(formEndpoint, 'post', payload, {
                    headers
                });
                const submitResponse = response.data;
                if (this.isSubmitResponseSuccessful(submitResponse)) {
                    this.setState({
                        submitStatus: 'success',
                        form: initialState,
                        submitted: false,
                        submitErrorCode: undefined
                    });
                } else {
                    this.setState({
                        submitStatus: 'error',
                        submitErrorCode: this.getSubmitErrorCode(submitResponse)
                    });
                }
            } catch (error) {
                this.setState({
                    submitStatus: 'error',
                    submitErrorCode: undefined
                });
            }
        }
    };

    public render(): JSX.Element | null {
        const { className } = this.props.config;
        return this.props.renderView({
            ...this.props,
            form: this.state.form,
            errors: this.state.errors,
            submitted: this.state.submitted,
            submitStatus: this.state.submitStatus,
            submitErrorCode: this.state.submitErrorCode,
            className: classnames('cws-form', className),
            onChange: this.handleChange,
            onSubmit: this.handleSubmit
        });
    }
}

export default CwsForm;
